// eslint-disable-next-line import/no-cycle
import {
  selectionBasedLogicalOperators,
  numberLogicalOperators,
  dateStringLogicalOperators,
  dateNumberLogicalOperators,
} from '@/composables/segmentations/logicalOperators';
// eslint-disable-next-line import/no-cycle
import { fetchCampaignsBulk, fetchCampaignsAutomation } from '@/composables/segmentations/helpers/fetchData';

// Mirrors ChannelIntegerEnum. 5 (Messenger, deprecated) and 9 (Teaser, a popup
// display rather than a send) are left out: neither produces an open.
const getSendingChannels = () => [
  { label: 'stats.statsLabels.channelsIntegers.1', value: 1 },
  { label: 'stats.statsLabels.channelsIntegers.2', value: 2 },
  { label: 'stats.statsLabels.channelsIntegers.3', value: 3 },
  { label: 'stats.statsLabels.channelsIntegers.4', value: 4 },
  { label: 'stats.statsLabels.channelsIntegers.6', value: 6 },
  { label: 'stats.statsLabels.channelsIntegers.7', value: 7 },
  { label: 'stats.statsLabels.channelsIntegers.8', value: 8 },
];

// An open always happened in the past. The future-facing operators are also a
// perf trap: at_most_in_x_units wraps the column, killing index and partition
// pruning on tables holding every send of the shop.
const PAST_DATE_NUMBER_OPERATORS = [
  'dateNumber_x_units_ago_exactly',
  'dateNumber_x_units_ago_at_least',
  'dateNumber_x_units_ago_at_most',
  'dateNumber_x_units_ago_more_than',
  'dateNumber_x_units_ago_less_than',
];

const dateLeaf = (fieldId: string, label: string) => ({
  id: fieldId,
  label,
  logical_operators: [
    { type: 'dateString', data: dateStringLogicalOperators(fieldId) },
    { type: 'dateNumber', data: dateNumberLogicalOperators(fieldId, PAST_DATE_NUMBER_OPERATORS) },
  ],
});

const channelLeaf = (labelKey: string) => ({
  id: 'channel',
  label: labelKey,
  logical_operators: selectionBasedLogicalOperators('channel', getSendingChannels()),
});

// Passed in by callers building several segments at once, so the editor issues
// two requests instead of four.
export type CampaignOption = { label: string; value: number };

export const getOpenedBulkCampaignSegment = async (preloadedCampaigns?: CampaignOption[]) => {
  const campaignsBulk = preloadedCampaigns ?? await fetchCampaignsBulk();
  return {
    id: 'opened_an_email_bulk',
    label: 'segmentations.hasDone.list.opened_an_email_bulk.label',
    sub_properties: [
      channelLeaf('segmentations.hasDone.list.campaign.list.campaign_attributes.list.channel'),
      dateLeaf('date_creation', 'segmentations.hasDone.list.campaign.list.campaign_attributes.list.date_open'),
      {
        id: 'id_campaign_bulk',
        label: 'segmentations.hasDone.list.campaign.list.campaigns_concerned',
        logical_operators: selectionBasedLogicalOperators('id_campaign_bulk', campaignsBulk),
        multiple: true,
      },
      {
        id: 'nb_open',
        label: 'segmentations.hasDone.list.campaign.list.min_opens',
        logical_operators: numberLogicalOperators('nb_open', [], true),
        aggregate: 'count',
      },
    ],
  };
};

export const getClickedBulkCampaignSegment = async (preloadedCampaigns?: CampaignOption[]) => {
  const campaignsBulk = preloadedCampaigns ?? await fetchCampaignsBulk();
  return {
    id: 'clicked_in_email_bulk',
    label: 'segmentations.hasDone.list.clicked_in_email_bulk.label',
    sub_properties: [
      channelLeaf('segmentations.hasDone.list.campaign.list.campaign_attributes.list.channel'),
      dateLeaf('date_creation', 'segmentations.hasDone.list.campaign.list.campaign_attributes.list.date_click'),
      {
        id: 'id_campaign_bulk',
        label: 'segmentations.hasDone.list.campaign.list.campaigns_concerned',
        logical_operators: selectionBasedLogicalOperators('id_campaign_bulk', campaignsBulk),
        multiple: true,
      },
      {
        id: 'nb_click',
        label: 'segmentations.hasDone.list.campaign.list.min_clicks',
        logical_operators: numberLogicalOperators('nb_click', [], true),
        aggregate: 'count',
      },
    ],
  };
};

export const getOpenedAutomationCampaignSegment = async (preloadedCampaigns?: CampaignOption[]) => {
  const campaignsAutomation = preloadedCampaigns ?? await fetchCampaignsAutomation();
  return {
    id: 'opened_an_email_automation',
    label: 'segmentations.hasDone.list.opened_an_email_automation.label',
    sub_properties: [
      channelLeaf('segmentations.hasDone.list.scenario.list.scenario_attributes.list.channel'),
      dateLeaf('date_creation', 'segmentations.hasDone.list.scenario.list.scenario_attributes.list.date_open'),
      {
        id: 'id_campaign_automation',
        label: 'segmentations.hasDone.list.scenario.list.scenarios_concerned',
        logical_operators: selectionBasedLogicalOperators('id_campaign_automation', campaignsAutomation),
        multiple: true,
      },
      {
        id: 'nb_open',
        label: 'segmentations.hasDone.list.scenario.list.min_opens',
        logical_operators: numberLogicalOperators('nb_open', [], true),
        aggregate: 'count',
      },
    ],
  };
};

export const getClickedAutomationCampaignSegment = async (preloadedCampaigns?: CampaignOption[]) => {
  const campaignsAutomation = preloadedCampaigns ?? await fetchCampaignsAutomation();
  return {
    id: 'clicked_in_email_automation',
    label: 'segmentations.hasDone.list.clicked_in_email_automation.label',
    sub_properties: [
      channelLeaf('segmentations.hasDone.list.scenario.list.scenario_attributes.list.channel'),
      dateLeaf('date_creation', 'segmentations.hasDone.list.scenario.list.scenario_attributes.list.date_click'),
      {
        id: 'id_campaign_automation',
        label: 'segmentations.hasDone.list.scenario.list.scenarios_concerned',
        logical_operators: selectionBasedLogicalOperators('id_campaign_automation', campaignsAutomation),
        multiple: true,
      },
      {
        id: 'nb_click',
        label: 'segmentations.hasDone.list.scenario.list.min_clicks',
        logical_operators: numberLogicalOperators('nb_click', [], true),
        aggregate: 'count',
      },
    ],
  };
};
